import React, { useContext, useEffect, useRef, useState } from 'react'
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    Row,
    Table,
    UncontrolledDropdown
} from 'reactstrap'
import CurrencyFormat from 'react-currency-format'
import Moment from 'react-moment'
import toastr from 'toastr'
import { useHistory, useLocation } from 'react-router-dom'

import Breadcrumbs from '../../components/Global/Breadcrumbs'
import EgresoModal from '../../components/egresos/EgresoModal'
import ApiService from '../../data/services/ApiService'
import { GeneralContext } from '../../context/'
import {
    CATEGORIAS_EGRESO,
    STATUS_OPTIONS,
    getCategoriaLabel,
    statusLabels
} from '../../helpers/egresoFormHelper'

const kpiCard = {
    background: '#ffffff',
    border: '0.5px solid #e9ecef',
    borderRadius: 6,
    padding: '12px 14px'
}

const statusColor = {
    pendiente_comprobante: 'warning',
    pendiente_pago: 'info',
    pagada: 'success',
    cancelada: 'secondary'
}

const CurrencyValue = ({ value }) => (
    <CurrencyFormat
        value={value || 0}
        displayType="text"
        thousandSeparator
        prefix="$"
        decimalScale={2}
    />
)

const EgresosIndex = () => {
    const { setLoading } = useContext(GeneralContext)
    const location = useLocation()
    const history = useHistory()
    const uploadInputRef = useRef(null)
    const initialStatus = new URLSearchParams(location.search).get('status') || ''
    const [egresos, setEgresos] = useState([])
    const [resumen, setResumen] = useState({})
    const [filters, setFilters] = useState({
        status: initialStatus,
        categoria: '',
        deducible: '',
        fechaInicio: '',
        fechaFin: '',
        search: ''
    })
    const [modalOpen, setModalOpen] = useState(false)
    const [editingEgreso, setEditingEgreso] = useState(null)
    const [uploadTarget, setUploadTarget] = useState(null)

    const loadEgresos = async () => {
        setLoading(true)
        try {
            const response = await ApiService.getEgresos(filters)
            setEgresos(response.data.data.egresos || [])
            setResumen(response.data.data.resumen || {})
        } catch (err) {
            toastr.error(err.response?.data?.message || 'No pude cargar tus egresos.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadEgresos()
        // eslint-disable-next-line
    }, [filters])

    const updateFilter = (field, value) => {
        setFilters(current => ({ ...current, [field]: value }))
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingEgreso(null)
    }

    const handleModalSuccess = async () => {
        closeModal()
        await loadEgresos()
    }

    const markAsPaid = async (egreso) => {
        try {
            await ApiService.updateEgreso(egreso._id, { status: 'pagada' })
            toastr.success('Egreso marcado como pagado.')
            await loadEgresos()
        } catch (err) {
            toastr.error(err.response?.data?.message || 'No pude actualizar el egreso.')
        }
    }

    const deleteEgreso = async (egreso) => {
        if (!window.confirm('¿Eliminar este egreso?')) {
            return
        }

        try {
            await ApiService.deleteEgreso(egreso._id)
            toastr.success('Egreso eliminado.')
            await loadEgresos()
        } catch (err) {
            toastr.error(err.response?.data?.message || 'No pude eliminar el egreso.')
        }
    }

    const openXmlUpload = (egreso) => {
        setUploadTarget(egreso)
        if (uploadInputRef.current) {
            uploadInputRef.current.value = ''
            uploadInputRef.current.click()
        }
    }

    const handleXmlUpload = async (event) => {
        const file = event.target.files?.[0]
        if (!file || !uploadTarget) return

        const formData = new FormData()
        formData.append('xml', file)

        try {
            await ApiService.updateEgreso(uploadTarget._id, formData)
            toastr.success('XML agregado al egreso.')
            setUploadTarget(null)
            await loadEgresos()
        } catch (err) {
            toastr.error(err.response?.data?.message || 'No pude subir el XML.')
        }
    }

    const downloadXml = (egreso) => {
        const blob = new Blob([egreso.xml], { type: 'application/xml' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${egreso.uuid || egreso._id}.xml`
        document.body.appendChild(link)
        link.click()
        link.parentNode.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    const viewXml = (egreso) => {
        const blob = new Blob([egreso.xml], { type: 'application/xml' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank', 'noopener,noreferrer')
        setTimeout(() => window.URL.revokeObjectURL(url), 3000)
    }

    const handleDescargarZip = async (egresoId, uuid) => {
        try {
            const response = await ApiService.descargarEgreso(egresoId)
            const url = URL.createObjectURL(response.data)
            const a = document.createElement('a')
            a.href = url
            a.download = `${uuid || egresoId}.zip`
            a.click()
            URL.revokeObjectURL(url)
        } catch (err) {
            toastr.error(err.response?.data?.message || 'No pude descargar el archivo.')
        }
    }

    return (
        <div className="page">
            <Breadcrumbs title="Egresos" breadcrumbItem="Egresos" parentLink="/egresos" />

            <Card>
                <CardBody>
                    <Row className="align-items-center">
                        <Col md={8} xs={12}>
                            <h3 style={{ marginBottom: 4 }}>Egresos</h3>
                            <p style={{ color: '#74788d', marginBottom: 0 }}>Tus gastos y facturas recibidas</p>
                        </Col>
                        <Col md={4} xs={12} className="text-md-right mt-3 mt-md-0">
                            <Button color="primary" onClick={() => setModalOpen(true)}>
                                Nuevo egreso
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <Row>
                <Col md={3} xs={12}>
                    <div style={kpiCard}>
                        <div style={{ fontSize: 12, color: '#74788d' }}>Total gastado</div>
                        <strong><CurrencyValue value={resumen.totalGastado} /></strong>
                    </div>
                </Col>
                <Col md={3} xs={12}>
                    <div style={kpiCard}>
                        <div style={{ fontSize: 12, color: '#74788d' }}>Total deducible este mes</div>
                        <strong><CurrencyValue value={resumen.totalDeducible} /></strong>
                    </div>
                </Col>
                <Col md={3} xs={12}>
                    <div style={{ ...kpiCard, borderColor: resumen.pendientesComprobante > 0 ? '#f1b44c' : '#e9ecef' }}>
                        <div style={{ fontSize: 12, color: '#74788d' }}>Pendientes de comprobante</div>
                        <strong>{resumen.pendientesComprobante || 0}</strong>
                    </div>
                </Col>
                <Col md={3} xs={12}>
                    <div style={kpiCard}>
                        <div style={{ fontSize: 12, color: '#74788d' }}>Pendientes de pago</div>
                        <strong>{resumen.pendientesPago || 0}</strong>
                    </div>
                </Col>
            </Row>

            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col md={12} xs={12} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {STATUS_OPTIONS.map(option => (
                                <Button
                                    key={option.value || 'all'}
                                    size="sm"
                                    color={filters.status === option.value ? 'primary' : 'light'}
                                    onClick={() => updateFilter('status', option.value)}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={3} xs={12}>
                            <Input type="select" value={filters.categoria} onChange={(event) => updateFilter('categoria', event.target.value)}>
                                <option value="">Todas las categorías</option>
                                {CATEGORIAS_EGRESO.map(categoria => (
                                    <option key={categoria.value} value={categoria.value}>{categoria.label}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={2} xs={12}>
                            <Input type="select" value={filters.deducible} onChange={(event) => updateFilter('deducible', event.target.value)}>
                                <option value="">Deducible: todos</option>
                                <option value="true">Deducibles</option>
                                <option value="false">No deducibles</option>
                            </Input>
                        </Col>
                        <Col md={2} xs={12}>
                            <Input type="date" value={filters.fechaInicio} onChange={(event) => updateFilter('fechaInicio', event.target.value)} />
                        </Col>
                        <Col md={2} xs={12}>
                            <Input type="date" value={filters.fechaFin} onChange={(event) => updateFilter('fechaFin', event.target.value)} />
                        </Col>
                        <Col md={3} xs={12}>
                            <Input placeholder="Buscar emisor o descripción" value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} />
                        </Col>
                    </Row>

                    <input ref={uploadInputRef} type="file" accept=".xml" style={{ display: 'none' }} onChange={handleXmlUpload} />

                    <div className="table-responsive">
                        <Table>
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Emisor / Descripción</th>
                                    <th>Categoría</th>
                                    <th>Deducible</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {egresos.map(egreso => (
                                    <tr key={egreso._id}>
                                        <td><Moment format="DD/MM/YYYY">{egreso.fechaGasto}</Moment></td>
                                        <td>
                                            <strong>{egreso.emisor?.nombre || egreso.descripcion || 'Egreso sin emisor'}</strong>
                                            {egreso.importadoDelSAT === true && (
                                                <Badge color="light" title="Importado del Portal SAT" style={{ marginLeft: 6, color: '#495057' }}>SAT</Badge>
                                            )}
                                            <div style={{ fontSize: 12, color: '#74788d' }}>
                                                {egreso.emisor?.rfc || egreso.descripcion || 'Sin descripción'}
                                            </div>
                                        </td>
                                        <td><Badge color="secondary">{getCategoriaLabel(egreso.categoria)}</Badge></td>
                                        <td>
                                            <Badge color={egreso.deducible ? 'success' : 'secondary'}>
                                                {egreso.deducible ? 'Deducible' : 'No deducible'}
                                            </Badge>
                                        </td>
                                        <td><CurrencyValue value={egreso.total} /></td>
                                        <td><Badge color={statusColor[egreso.status] || 'secondary'}>{statusLabels[egreso.status] || egreso.status}</Badge></td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <UncontrolledDropdown>
                                                <DropdownToggle href="#" className="card-drop" tag="i">
                                                    <i className="mdi mdi-dots-horizontal font-size-18"></i>
                                                </DropdownToggle>
                                                <DropdownMenu right>
                                                    {egreso.tipoDeComprobante !== 'P' && egreso.xml && (
                                                        <DropdownItem href="#" onClick={(e) => {
                                                            e.preventDefault()
                                                            history.push(`/egresos/${egreso._id}/detalle`)
                                                        }}>
                                                            <i className="mdi mdi-note font-size-16 text-primary mr-2"></i> Ver factura
                                                        </DropdownItem>
                                                    )}
                                                    {egreso.tipoDeComprobante !== 'P' && egreso.xml && (
                                                        <DropdownItem href="#" onClick={(e) => {
                                                            e.preventDefault()
                                                            handleDescargarZip(egreso._id, egreso.uuid)
                                                        }}>
                                                            <i className="bx bxs-file-archive font-size-16 text-success mr-2"></i> Descargar factura (ZIP)
                                                        </DropdownItem>
                                                    )}
                                                    {egreso.complementoId && (
                                                        <>
                                                            <div className="dropdown-divider"></div>
                                                            <DropdownItem href="#" onClick={(e) => {
                                                                e.preventDefault()
                                                                history.push(`/egresos/${egreso.complementoId}/detalle`)
                                                            }}>
                                                                <i className="mdi mdi-cash-check font-size-16 text-primary mr-2"></i> Ver complemento
                                                            </DropdownItem>
                                                            <DropdownItem href="#" onClick={(e) => {
                                                                e.preventDefault()
                                                                handleDescargarZip(egreso.complementoId, null)
                                                            }}>
                                                                <i className="bx bxs-file-archive font-size-16 text-success mr-2"></i> Descargar complemento (ZIP)
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                    {egreso.importadoDelSAT !== true && egreso.status !== 'pagada' && (
                                                        <>
                                                            <div className="dropdown-divider"></div>
                                                            <DropdownItem href="#" onClick={(e) => {
                                                                e.preventDefault()
                                                                setEditingEgreso(egreso)
                                                                setModalOpen(true)
                                                            }}>
                                                                <i className="mdi mdi-pencil font-size-16 text-primary mr-2"></i> Editar
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                    {egreso.status === 'pendiente_comprobante' && (
                                                        <DropdownItem href="#" onClick={(e) => {
                                                            e.preventDefault()
                                                            openXmlUpload(egreso)
                                                        }}>
                                                            <i className="bx bx-upload font-size-16 text-warning mr-2"></i> Subir XML
                                                        </DropdownItem>
                                                    )}
                                                    {egreso.status === 'pendiente_pago' && (
                                                        <DropdownItem href="#" onClick={(e) => {
                                                            e.preventDefault()
                                                            markAsPaid(egreso)
                                                        }}>
                                                            <i className="mdi mdi-check-circle font-size-16 text-success mr-2"></i> Marcar pagada
                                                        </DropdownItem>
                                                    )}
                                                </DropdownMenu>
                                            </UncontrolledDropdown>
                                        </td>
                                    </tr>
                                ))}
                                {egresos.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ color: '#74788d' }}>
                                            <p>No tienes egresos registrados.</p>
                                            {/* <p>Te recomendamos sincronizar con el SAT para importar las facturas recibidas.</p> */}

                                            {/* <a href='/configuracion/sync-sat' className='btn btn-primary'>Sincronizar con SAT</a> */}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>

            <EgresoModal
                isOpen={modalOpen}
                mode={editingEgreso ? 'edit' : 'create'}
                egreso={editingEgreso}
                onClose={closeModal}
                onSuccess={handleModalSuccess}
            />
        </div>
    )
}

export default EgresosIndex
